import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { CalendarSurfaceDefaultComponent, combineHandlers } from 'owa-calendar-surface-core';
import {
    CalendarSurfaceStore,
    CalendarSurfaceTransformProps,
    WorkingHoursContent,
    MultiWorkingHoursContent,
    useColumnSelectionHooks,
    SurfaceItem,
} from 'owa-calendar-surface-dategrid';
import { OwaDate } from 'owa-datetime';
import ColumnFreeBusyContent from '../logic/content/column/ColumnFreeBusyContent';
import WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';

export interface CalendarInDayColumnAdditionalPropsType {
    store: CalendarSurfaceStore;
    config: CalendarSurfaceTransformProps;
    calendarFolderId?: string;
    date: OwaDate;
    busyModelsProvider: () => SurfaceItem[];
    index: number;
    calendarWorkingHours?: WorkingHoursType;
}

export default observer(function CalendarInDayColumnComponent({
    className,
    style,
    children,
    handlers,
    additionalProps,
}: {
    className?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
    handlers?: React.DOMAttributes<HTMLElement>;
    additionalProps: CalendarInDayColumnAdditionalPropsType;
}) {
    const {
        config,
        date,
        busyModelsProvider,
        store,
        calendarFolderId,
        index,
        calendarWorkingHours,
    } = additionalProps;

    let finalChildren = [
        <ColumnFreeBusyContent
            key="freeBusyContent"
            itemsLayout={config.itemsLayout}
            busyModelsProvider={busyModelsProvider}
            template={config.freeBusyTemplate}
        />,
        <React.Fragment key={'originalChildren'}>{children}</React.Fragment>,
    ];

    if (!config.disableWorkingHours) {
        const hasSpecificWorkDays = config.getCalendarSpecificWorkDays?.(calendarFolderId);

        if (!hasSpecificWorkDays) {
            finalChildren.push(
                <WorkingHoursContent
                    key={'workingHoursContent'}
                    surfaceStore={store}
                    modelDate={date}
                    calendarWorkingHours={calendarWorkingHours}
                />
            );
        } else {
            const {
                workDaysWiseHoursStartTimeInMinutes,
                workDaysWiseHoursEndTimeInMinutes,
            } = hasSpecificWorkDays;

            if (workDaysWiseHoursStartTimeInMinutes && workDaysWiseHoursEndTimeInMinutes) {
                finalChildren.push(
                    <MultiWorkingHoursContent
                        key={'workingHoursContent'}
                        surfaceStore={store}
                        modelDate={date}
                        workDaysWiseHoursStartTimeInMinutes={workDaysWiseHoursStartTimeInMinutes}
                        workDaysWiseHoursEndTimeInMinutes={workDaysWiseHoursEndTimeInMinutes}
                    />
                );
            } else {
                finalChildren.push(
                    <WorkingHoursContent
                        key={'workingHoursContent'}
                        surfaceStore={store}
                        modelDate={date}
                        calendarWorkingHours={calendarWorkingHours}
                    />
                );
            }
        }
    }

    const columnSelectionHooks = useColumnSelectionHooks({
        surfaceStore: store,
        config: config,
        columnIndex: index,
        calendarFolderId: calendarFolderId,
        disableColumnSelection: config.disableColumnSelection,
    });

    finalChildren.push(
        <React.Fragment key={'columnSelectionContent'}>
            {columnSelectionHooks.children}
        </React.Fragment>
    );

    const handlersToAttach = combineHandlers(handlers, columnSelectionHooks.handlers);

    return (
        <CalendarSurfaceDefaultComponent
            className={className}
            style={style}
            handlers={handlersToAttach}>
            {finalChildren}
        </CalendarSurfaceDefaultComponent>
    );
});
